import { ChevronsUpDown } from "lucide-react";
import type { SocialPostCopyTraceV2 } from "@runcheetah/shared";

import { JsonBlock } from "@/components/common/json-block";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { formatSnakeLabel } from "@/lib/labels";

const formatGenerator = (generator: SocialPostCopyTraceV2["generator"]): string =>
  formatSnakeLabel(generator);

const formatConfidence = (confidence: number): string => `${Math.round(confidence * 100)}%`;

export function CopyTracePanel({
  trace,
  title = "Copy Decisions",
}: {
  trace: SocialPostCopyTraceV2 | null | undefined;
  title?: string;
}) {
  if (!trace) {
    return null;
  }

  return (
    <div className="space-y-3 rounded-3xl border bg-background p-4 shadow-xs">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">{title}</h3>
          <p className="text-xs text-muted-foreground">
            {formatGenerator(trace.generator)} · {trace.decisions.length} decision
            {trace.decisions.length === 1 ? "" : "s"}
          </p>
        </div>
        <span className="rounded-full bg-muted px-2 py-0.5 font-mono text-[11px] text-muted-foreground">
          v{trace.version}
        </span>
      </div>

      <div className="space-y-3">
        {trace.decisions.map((decision, index) => (
          <div
            key={`${decision.target}-${index}`}
            className="space-y-3 rounded-2xl border bg-muted/20 p-3"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-mono text-[11px] text-muted-foreground">{decision.target}</div>
                <div className="mt-1 whitespace-pre-wrap text-sm font-medium leading-6 text-foreground">
                  {decision.selectedText}
                </div>
              </div>
              <span className="rounded-full bg-background px-2 py-1 font-mono text-[11px] text-muted-foreground">
                {formatConfidence(decision.confidence)}
              </span>
            </div>

            <p className="text-sm leading-6 text-muted-foreground">{decision.rationale}</p>

            {decision.rejectedAlternatives.length > 0 ? (
              <div className="space-y-1 rounded-xl bg-background/80 px-3 py-2 text-xs text-muted-foreground">
                <div className="font-semibold text-foreground/70">Rejected alternatives</div>
                {decision.rejectedAlternatives.map((alternative, alternativeIndex) => (
                  <div key={`${alternative.text}-${alternativeIndex}`}>
                    <span className="text-foreground/80">{alternative.text}</span>:{" "}
                    {alternative.reason}
                  </div>
                ))}
              </div>
            ) : null}

            <div className="space-y-2">
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Cited Data Points
              </div>
              <div className="grid gap-2 lg:grid-cols-2">
                {decision.citations.map((citation, citationIndex) => (
                  <div
                    key={`${citation.sourcePath}-${citationIndex}`}
                    className="rounded-xl border bg-background px-3 py-2"
                  >
                    <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                      <span className="rounded-full bg-muted px-2 py-0.5 font-mono">
                        {citation.sourceType}
                      </span>
                      <span className="font-mono">{citation.sourcePath}</span>
                    </div>
                    <div className="mt-2 text-xs font-semibold text-foreground">
                      {citation.label}
                    </div>
                    <p className="mt-1 text-xs leading-5 text-foreground/72">
                      {citation.valueExcerpt}
                    </p>
                    {citation.relevance ? (
                      <p className="mt-2 text-xs leading-5 text-muted-foreground">
                        {citation.relevance}
                      </p>
                    ) : null}
                    {citation.sourceId ? (
                      <div className="mt-2 break-all font-mono text-[10px] text-muted-foreground">
                        {citation.sourceId}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {trace.warnings.length > 0 ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {trace.warnings.join(" · ")}
        </div>
      ) : null}

      <Collapsible className="rounded-2xl border bg-background/80">
        <CollapsibleTrigger className="flex w-full items-center justify-between px-3 py-2 text-left text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          Raw Copy Trace JSON
          <ChevronsUpDown className="size-3.5" aria-hidden="true" />
        </CollapsibleTrigger>
        <CollapsibleContent className="border-t p-3">
          <JsonBlock value={trace} />
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
